import React, { useEffect, useState, useCallback, useMemo } from 'react';
import cookie from 'js-cookie';
import { trackEvent } from 'user-analytics';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import useIsMobile from 'hooks/useIsMobile';
import { isWebView } from 'utils/webview';
import './SmartBanner.scss';
import { viewResultsData } from 'metrics/user-analytics/data';

const COOKIE_NAME = 'smartbanner_exited';
const COOKIE_EXPIRY_DAYS = 7;

/**
 * Returns whether the smart banner was previously dismissed (cookie set).
 * @returns {boolean} True if the banner was dismissed, false otherwise.
 */
const getIsDismissed = () => {
  try {
    return Boolean(cookie.get(COOKIE_NAME));
  } catch (error) {
    console.error('Error getting cookie:', error);
    return false;
  }
};

/**
 * Detects if the current device is running Android.
 * @returns {boolean} True if Android, false otherwise.
 */
function isAndroid() {
  if (typeof navigator === 'undefined') return false;
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /android/i.test(userAgent);
}

/**
 * Detects if the current device is running iOS.
 * @returns {boolean} True if iOS, false otherwise.
 */
function isIos() {
  if (typeof navigator === 'undefined') return false;
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /iphone|ipad|ipod/i.test(userAgent);
}

/**
 * SmartBanner component.
 * Displays a banner prompting mobile users to download the native app on both iOS and Android.
 * @returns {JSX.Element|null} The smart banner on mobile (iOS/Android), or null on desktop/webview.
 */
const SmartBanner = () => {
  const { smartBanner } = useWhitelabelEnvs();
  const isMobile = useIsMobile();
  const [isVisible, setIsVisible] = useState(true);
  const isAndroidDevice = useMemo(() => isAndroid(), []);
  const isIosDevice = useMemo(() => isIos(), []);

  const isEnabled = smartBanner?.enabled;
  const bannerConfig = smartBanner?.props || {};

  const enabledPlatformsStr = bannerConfig['enabled-platforms'] || 'android,ios';
  const enabledPlatforms = enabledPlatformsStr
    .split(',')
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean);
  const isPlatformEnabled =
    (isAndroidDevice && enabledPlatforms.includes('android')) ||
    (isIosDevice && enabledPlatforms.includes('ios'));

  const isDismissed = getIsDismissed();

  const storeUrl = isAndroidDevice
    ? bannerConfig['button-url-google']
    : bannerConfig['button-url-apple'];
  const hasStoreUrl = Boolean(storeUrl);

  const shouldShowBanner =
    isEnabled &&
    isMobile &&
    (isAndroidDevice || isIosDevice) &&
    isPlatformEnabled &&
    hasStoreUrl &&
    !isWebView() &&
    !isDismissed &&
    isVisible;

  useEffect(() => {
    if (!shouldShowBanner) return;
    if (typeof MutationObserver === 'undefined' || !document?.body) {
      return;
    }

    const selectors = [
      'div.header-fixed',
      'div.overlay-view',
      'div.timer-block',
      'div.departure-trip',
      'div.timer-fixed',
      'div.home-container',
    ];

    /**
     * Add smartbanner-hack class to fixed/overlay elements so they shift when the banner is visible.
     */
    function addHackClass() {
      selectors.forEach((selector) => {
        const el = document.querySelector(selector);
        if (el && !el.classList.contains('smartbanner-hack')) {
          el.classList.add('smartbanner-hack');
        }
      });
    }

    addHackClass();

    const observer = new MutationObserver(() => {
      addHackClass();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      selectors.forEach((selector) => {
        const el = document.querySelector(selector);
        if (el) el.classList.remove('smartbanner-hack');
      });
    };
  }, [shouldShowBanner]);

  /**
   * Handle close button click.
   */
  const handleClose = useCallback(() => {
    try {
      cookie.set(COOKIE_NAME, '1', { expires: COOKIE_EXPIRY_DAYS });
    } catch (error) {
      console.error('Error setting cookie:', error);
    } finally {
      setIsVisible(false);
    }
  }, []);

  /**
   * Handle download button click.
   */
  const handleDownloadClick = useCallback(() => {
    try {
      cookie.set(COOKIE_NAME, '1', { expires: COOKIE_EXPIRY_DAYS });
    } catch (error) {
      console.error('Error setting cookie:', error);
    } finally {
      trackEvent('App Banner Clicked', viewResultsData());
      setIsVisible(false);
    }
  }, []);

  if (!shouldShowBanner) {
    return null;
  }

  const title = bannerConfig.title || '';
  const author = bannerConfig.author || '';
  const price = bannerConfig.price || '';
  const priceSuffix = isAndroidDevice
    ? bannerConfig['price-suffix-google']
    : bannerConfig['price-suffix-apple'];
  const buttonText = bannerConfig.button || 'VIEW';
  const iconUrl = isAndroidDevice
    ? bannerConfig['icon-google'] || ''
    : bannerConfig['icon-apple'] || '';
  const closeLabel = bannerConfig['close-label'] || 'Close';

  return (
    <div className="fixed top-0 left-0 right-0 z-[99] flex items-center px-3 py-2.5 bg-white shadow-sm">
      <button
        type="button"
        className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors"
        aria-label={closeLabel}
        onClick={handleClose}
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      <div
        className="flex-shrink-0 w-12 h-12 ml-1 rounded-xl bg-cover bg-center bg-no-repeat shadow-md"
        style={{ backgroundImage: iconUrl ? `url(${iconUrl})` : undefined }}
        aria-hidden="true"
      />

      <div className="flex-1 min-w-0 ml-3 mr-3">
        <div className="text-sm font-bold text-gray-900 truncate">{title}</div>
        {author && <div className="text-xs font-semibold text-gray-600 truncate">{author}</div>}
        {price && (
          <div className="text-xs text-gray-500 truncate">
            {price} {priceSuffix}
          </div>
        )}
      </div>

      <a
        href={storeUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex-shrink-0 px-3 py-1.5 font-bold text-center text-primary-regular bg-white hover:bg-primary-strong transition-colors border border-primary-regular rounded-md text-xs"
        onClick={handleDownloadClick}
      >
        {buttonText}
      </a>
    </div>
  );
};

export default SmartBanner;
